import { fetchStats } from '../website/votingApi'

const RE_PARSE_THRESH = /parseFloat\(value\)\s*>=\s*\d+/
const RE_TIERED_2 = /parseFloat\(value\)\s*>=\s*\d+\s*\?\s*'#[0-9A-Fa-f]{6}'\s*:\s*'#[0-9A-Fa-f]{6}'/
const RE_TIERED_3 = /parseFloat\(value\)\s*>=\s*\d+\s*\?\s*'#[0-9A-Fa-f]{6}'\s*:\s*parseFloat\(value\)\s*>=/
const RE_BINARY_TERNARY = /\?\s*'#[0-9A-Fa-f]{6}'\s*:\s*'#[0-9A-Fa-f]{6}'/

const TIER_HEX_POOL = [
  '#E63946', '#457B9D', '#2A9D8F', '#E9C46A', '#264653', '#8338EC', '#FF006E', '#3A86FF',
  '#FB5607', '#FFBE0B', '#06D6A0', '#118AB2', '#073B4C', '#EF476F', '#7209B7', '#4CC9F0',
  '#00BBF9', '#F15BB5', '#9B5DE5', '#00F5D4'
]

function hexToRgb (hex) {
  const x = hex.slice(1)
  return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)]
}

function colorDist (a, b) {
  const A = hexToRgb(a)
  const B = hexToRgb(b)
  return Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2])
}

function pairwiseOk (colors, minD) {
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      if (colorDist(colors[i], colors[j]) < minD) return false
    }
  }
  return true
}

function shufflePickN (n) {
  const a = [...TIER_HEX_POOL]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a.slice(0, n)
}

/** Picks n hex colors with RGB distance ≥ minD between every pair; falls back to evenly spaced hues. */
function pickDissimilarColors (n, minD = 100) {
  for (let t = 0; t < 450; t++) {
    const c = shufflePickN(n)
    if (pairwiseOk(c, minD)) return c
  }
  return pickHueSpreadHex(n)
}

function hslToHex (hDeg, s, l) {
  const h = (((hDeg % 360) + 360) % 360) / 360
  const hue2rgb = (p, q, t) => {
    let T = t
    if (T < 0) T += 1
    if (T > 1) T -= 1
    if (T < 1 / 6) return p + (q - p) * 6 * T
    if (T < 1 / 2) return q
    if (T < 2 / 3) return p + (q - p) * (2 / 3 - T) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = hue2rgb(p, q, h + 1 / 3)
  const g = hue2rgb(p, q, h)
  const b = hue2rgb(p, q, h - 1 / 3)
  const to = (x) =>
    Math.round(Math.min(255, Math.max(0, x * 255)))
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

function pickHueSpreadHex (n) {
  const base = Math.random() * 360
  const out = []
  for (let i = 0; i < n; i++) {
    out.push(hslToHex((base + (360 / n) * i) % 360, 0.72, 0.5))
  }
  return out
}

function podium (stats) {
  const items = Array.isArray(stats?.items) ? stats.items : []
  const v = (i) => Math.max(0, Math.floor(Number(items[i]?.votes) || 0))
  return { v1: v(0), v2: v(1), v3: v(2) }
}

function tieredState (p) {
  const { v1, v2, v3 } = p
  if (v1 <= 0 || v2 <= 0) return null
  if (v1 - v2 <= 1) return { mode: 'two', cutoff: Math.max(1, v2), ...p }
  let hi = Math.max(1, v2 + 1)
  const lo = Math.max(1, v3 + 1)
  if (v1 < hi) hi = Math.max(1, v1)
  if (lo >= hi) return { mode: 'two', cutoff: Math.max(1, v2), ...p }
  return { mode: 'three', tierHigh: hi, tierLow: lo, ...p }
}

function withTierColors (s) {
  if (!s) return null
  if (s.mode === 'two') {
    const [cHi, cLo] = pickDissimilarColors(2)
    return { ...s, cHi, cLo }
  }
  const [cHi, cMid, cLo] = pickDissimilarColors(3)
  return { ...s, cHi, cMid, cLo }
}

function tieredFormula (s) {
  if (!s?.cHi) return null
  if (s.mode === 'two') return `parseFloat(value)>=${s.cutoff}?'${s.cHi}':'${s.cLo}'`
  return `parseFloat(value)>=${s.tierHigh}?'${s.cHi}':parseFloat(value)>=${s.tierLow}?'${s.cMid}':'${s.cLo}'`
}

function binaryMeta (p) {
  const { v1, v2, v3 } = p
  if (v1 <= 0 || v2 <= 0) return null
  if (v1 - v2 <= 1) return { cutoff: Math.max(1, v2), kind: 'close', ...p }
  if (v3 > 0) return { cutoff: Math.max(1, v3 + 1), kind: 'spread', ...p }
  return { cutoff: Math.max(1, v2), kind: 'v2only', ...p }
}

function withBinaryColors (b) {
  if (!b) return null
  const [cStrong, cMuted] = pickDissimilarColors(2)
  return { ...b, cStrong, cMuted }
}

function cityLabel (panel) {
  const m = (panel.reasoning || '').match(/CITY\s*(\d+)/i)
  return m ? `CITY ${m[1]}` : 'this row'
}

function reasoningTiered (panel, s) {
  const L = cityLabel(panel)
  const { v1, v2, v3 } = s
  if (s.mode === 'two') {
    return `Color-codes ${L}: upper tier ≥${s.cutoff} (${s.cHi}), lower (${s.cLo}). 1st ${v1} vs 2nd ${v2} (single split). Third: ${v3}.`
  }
  return `Color-codes ${L}: high ≥${s.tierHigh} (${s.cHi}), mid ${s.tierLow}–${s.tierHigh - 1} (${s.cMid}), low <${s.tierLow} (${s.cLo}). Podium ${v1}/${v2}/${v3}.`
}

function reasoningBinary (m) {
  const { cutoff, v1, v2, v3, kind, cStrong, cMuted } = m
  const r = `${v1}/${v2}/${v3}`
  const pair = `${cStrong} (above cutoff) / ${cMuted} (below)`
  if (kind === 'close') return `Votes ≥${cutoff} → ${pair}. 1st/2nd within 1 vote (${r}).`
  if (kind === 'v2only') return `Votes ≥${cutoff} → ${pair}. 2nd-place bar (${r}).`
  return `Votes ≥${cutoff} → ${pair}. Above 3rd at ${v3}; ${r}.`
}

function isTieredVotePanel (f, panel) {
  if (typeof f !== 'string' || !f.includes('parseFloat(value)') || f.includes('#228B22')) return false
  if (RE_TIERED_3.test(f) || f.includes('#90EE90')) return true
  const r = panel.reasoning || ''
  return RE_TIERED_2.test(f) && /Color-codes/i.test(r)
}

function isBinaryPopularity (f, panel) {
  if (typeof f !== 'string' || !f.includes('parseFloat(value)')) return false
  if (isTieredVotePanel(f, panel)) return false
  if (RE_TIERED_3.test(f) || f.includes('#90EE90')) return false
  const r = panel.reasoning || ''
  if (f.includes('#228B22')) return true
  if (/Changes the text color to green or gold/i.test(r)) return true
  if (/^Votes ≥/i.test(r)) return true
  return RE_PARSE_THRESH.test(f) && RE_BINARY_TERNARY.test(f)
}

function patchBinaryFormula (f0, cutoff, cStrong, cMuted) {
  let s = f0.replace(RE_PARSE_THRESH, `parseFloat(value) >= ${cutoff}`)
  s = s.replace(RE_BINARY_TERNARY, `?'${cStrong}' : '${cMuted}'`)
  return s
}

function applyFormula (panel, parsed, formula, reasoning) {
  return { ...panel, formula: JSON.stringify({ ...parsed, formula }), reasoning }
}

function patchPanel (panel, tiered, binary) {
  if (panel.type !== 'formulaic' || !panel.formula) return panel
  let parsed
  try {
    parsed = JSON.parse(panel.formula)
  } catch {
    return panel
  }
  const f0 = parsed.formula
  if (typeof f0 !== 'string') return panel

  if (tiered && isTieredVotePanel(f0, panel)) {
    const next = tieredFormula(tiered)
    if (next && next !== f0) return applyFormula(panel, parsed, next, reasoningTiered(panel, tiered))
  }

  if (binary && isBinaryPopularity(f0, panel)) {
    const next = patchBinaryFormula(f0, binary.cutoff, binary.cStrong, binary.cMuted)
    if (next !== f0) return applyFormula(panel, parsed, next, reasoningBinary(binary))
  }

  return panel
}

/** Patch pregenerated vote color formulas from live voting-stats podium. */
export async function applyDynamicVoteThresholdsToPanels (panels) {
  if (!Array.isArray(panels) || !panels.length) return panels
  let stats
  try {
    stats = await fetchStats(true)
  } catch (e) {
    console.warn('applyDynamicVoteThresholdsToPanels: fetchStats failed', e)
    return panels
  }
  const p = podium(stats)
  const tiered = withTierColors(tieredState(p))
  const binary = withBinaryColors(binaryMeta(p))
  return panels.map((x) => patchPanel(x, tiered, binary))
}
